import React, { useReducer, useState, useCallback, useMemo } from 'react';
import {
  Box, Flex, Text, Button, Input, Spinner, useColorModeValue, Card, CardBody, useToast,
  useDisclosure, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody,
} from '@chakra-ui/react';
import { MdQueueMusic } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { apiClient } from '../../services/apiClient';
import { TagMultiSelect } from '../../components/forms/TagMultiSelect';
import { useLibraryTags } from './hooks/useLibraryTags';
import { batchAddTracksToPlaylist, fetchCuratedPlaylists, type LibraryTrackDto } from '../../api/libraryApi';
import { PlaylistMultiSelect } from './components/PlaylistMultiSelect';

type FileStatus = 'pending' | 'uploading' | 'done' | 'error';

interface QueuedFile {
  id: string;
  file: File;
  title: string;
  status: FileStatus;
  error?: string;
}

type QueueAction =
  | { type: 'ADD_FILES'; files: File[] }
  | { type: 'REMOVE_FILE'; id: string }
  | { type: 'UPDATE_TITLE'; id: string; title: string }
  | { type: 'SET_STATUS'; id: string; status: FileStatus; error?: string }
  | { type: 'CLEAR_COMPLETED' };

function extractTitle(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ').trim();
}

const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function queueReducer(state: QueuedFile[], action: QueueAction): QueuedFile[] {
  switch (action.type) {
    case 'ADD_FILES':
      return [
        ...state,
        ...action.files.map((file) => ({
          id: crypto.randomUUID(),
          file,
          title: extractTitle(file.name),
          status: 'pending' as const,
        })),
      ];
    case 'REMOVE_FILE':
      return state.filter((f) => f.id !== action.id);
    case 'UPDATE_TITLE':
      return state.map((f) => (f.id === action.id ? { ...f, title: action.title } : f));
    case 'SET_STATUS':
      return state.map((f) =>
        f.id === action.id ? { ...f, status: action.status, error: action.error } : f,
      );
    case 'CLEAR_COMPLETED':
      return state.filter((f) => f.status !== 'done');
    default:
      return state;
  }
}

function FileRow({
  item,
  onRemove,
  onTitleChange,
  disabled,
}: {
  item: QueuedFile;
  onRemove: () => void;
  onTitleChange: (title: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const statusColor = {
    pending: useColorModeValue('secondaryGray.600', 'secondaryGray.500'),
    uploading: 'blue.500',
    done: 'green.500',
    error: 'red.500',
  }[item.status];

  const statusLabel = {
    pending: t('admin.library.fileStatusPending'),
    uploading: t('admin.library.fileStatusUploading'),
    done: t('admin.library.fileStatusDone'),
    error: t('admin.library.fileStatusError'),
  }[item.status];

  return (
    <Box>
      <Flex
        p="10px"
        borderBottom="1px solid"
        borderColor={borderColor}
        align="center"
        gap={3}
        data-testid={`file-row-${item.id}`}
      >
        <Text flex={2} noOfLines={1} fontSize="sm" color={textColor} fontWeight="500">
          {item.file.name}
        </Text>
        <Box flex={2}>
          <Input
            size="sm"
            borderRadius="10px"
            value={item.title}
            onChange={(e) => onTitleChange(e.target.value)}
            isDisabled={item.status !== 'pending'}
            data-testid={`input-title-${item.id}`}
          />
        </Box>
        <Text flex={1} fontSize="sm" textAlign="right" color={secondaryColor}>
          {formatSize(item.file.size)}
        </Text>
        <Flex flex={1} align="center" gap={1} justify="center">
          {item.status === 'uploading' && <Spinner size="sm" />}
          <Text color={statusColor} fontSize="sm" fontWeight="500">
            {statusLabel}
          </Text>
        </Flex>
        <Flex w="90px" justify="center">
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={onRemove}
            isDisabled={item.status === 'uploading' || disabled}
            data-testid={`btn-remove-${item.id}`}
          >
            {t('admin.library.removeFile')}
          </Button>
        </Flex>
      </Flex>
      {item.status === 'error' && item.error && (
        <Text
          color="red.500"
          fontSize="xs"
          mt={1}
          px={3}
          data-testid={`error-message-${item.id}`}
        >
          {item.error}
        </Text>
      )}
    </Box>
  );
}

export function MultiUploadPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [queue, dispatch] = useReducer(queueReducer, []);
  const [isUploading, setIsUploading] = useState(false);
  const [rejectedMessage, setRejectedMessage] = useState<string | null>(null);
  // Batch tags (FR-059) — applied to every file in the upload batch.
  const [batchTags, setBatchTags] = useState<string[]>([]);
  // Pre-upload "add to playlists" picker (FR-075): curated playlists the freshly
  // uploaded tracks are dropped into once the batch completes. Multi-select.
  const [selectedPlaylistIds, setSelectedPlaylistIds] = useState<string[]>([]);
  const playlistDrawer = useDisclosure();
  const { data: tagPool = [] } = useLibraryTags();
  // Shares the picker's query cache so we can resolve the selected ids to names
  // for the one-line summary without a second fetch.
  const { data: curatedPlaylists } = useQuery({
    queryKey: ['curated-playlists-picker'],
    queryFn: fetchCuratedPlaylists,
  });
  // Selected playlist names, in selection order, as a single comma-separated string.
  const selectedPlaylistNames = useMemo(() => {
    if (!curatedPlaylists || selectedPlaylistIds.length === 0) return '';
    const nameById = new Map(curatedPlaylists.map((p) => [p.id, p.name]));
    return selectedPlaylistIds
      .map((pid) => nameById.get(pid))
      .filter((name): name is string => Boolean(name))
      .join(', ');
  }, [curatedPlaylists, selectedPlaylistIds]);
  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const dropzoneBg = useColorModeValue('secondaryGray.300', 'navy.700');
  const dropzoneActiveBg = useColorModeValue('brand.50', 'brand.900');
  const dropzoneBorder = useColorModeValue('secondaryGray.400', 'whiteAlpha.300');
  const dropzoneActiveBorder = useColorModeValue('brand.400', 'brand.400');
  const headerBg = useColorModeValue('secondaryGray.300', 'navy.700');

  const onDrop = useCallback(
    (accepted: File[]) => {
      setRejectedMessage(null);
      dispatch({ type: 'ADD_FILES', files: accepted });
    },
    [],
  );

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const count = rejections.length;
    const reasons = new Set(rejections.flatMap((r) => r.errors.map((e) => e.code)));

    let message = `${count} file${count > 1 ? 's' : ''} rejected: `;

    if (reasons.has('file-invalid-type')) {
      message += 'only MP3, WAV, AAC, and FLAC formats are supported';
    } else if (reasons.has('file-too-large')) {
      message += t('admin.library.rejectionFileTooLarge');
    } else {
      message += 'invalid file(s)';
    }

    setRejectedMessage(message);
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/aac': ['.aac'],
      'audio/flac': ['.flac'],
    },
    multiple: true,
    maxSize: MAX_FILE_SIZE_BYTES,
    onDrop,
    onDropRejected,
    disabled: isUploading,
  });

  const handleUploadAll = async () => {
    setIsUploading(true);

    const pending = queue.filter((f) => f.status === 'pending' || f.status === 'error');
    // Collect the ids of tracks created by this batch so we can drop them into the
    // selected playlists once the upload completes (FR-075). Pending/processing tracks
    // are included — they may be added immediately (no waiting on processing).
    const newlyUploadedIds: string[] = [];

    for (const item of pending) {
      dispatch({ type: 'SET_STATUS', id: item.id, status: 'uploading' });

      try {
        const formData = new FormData();
        formData.append('file', item.file);
        // NOTE: MultiUploadPage sends per-file title plus batch-level tags (FR-059).
        // Other metadata (artist, genre, mood, tempo) is still edited after upload on
        // the track detail screen — keeps the multi-file upload UX simple. Tags are
        // an exception because applying the same tag set to a batch is the common
        // curator workflow (e.g. uploading a "chill / lofi" record drop).
        formData.append('title', item.title || item.file.name);
        // Repeated `tags` form field; the backend reads `Form["tags"]` as a multi-value
        // string and normalizes server-side (lowercase / trim / dedupe / HTML-strip).
        for (const tag of batchTags) {
          formData.append('tags', tag);
        }
        const created = await apiClient.upload<LibraryTrackDto>('/admin/library/upload', formData);
        if (created?.id) newlyUploadedIds.push(created.id);
        dispatch({ type: 'SET_STATUS', id: item.id, status: 'done' });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        dispatch({ type: 'SET_STATUS', id: item.id, status: 'error', error: message });
      }
    }

    setIsUploading(false);
    queryClient.invalidateQueries({ queryKey: ['admin-library'] });

    // Drop the freshly-uploaded tracks into the playlists picked before upload.
    if (newlyUploadedIds.length > 0 && selectedPlaylistIds.length > 0) {
      try {
        await Promise.all(
          selectedPlaylistIds.map((pid) => batchAddTracksToPlaylist(pid, newlyUploadedIds)),
        );
        toast({
          title: t('admin.library.uploadToPlaylists.added', { count: newlyUploadedIds.length }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        queryClient.invalidateQueries({ queryKey: ['admin-playlist'] });
      } catch {
        toast({
          title: t('admin.library.uploadToPlaylists.addFailed'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    }
  };

  const hasPending = queue.some((f) => f.status === 'pending' || f.status === 'error');
  const hasDone = queue.some((f) => f.status === 'done');

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} maxW="900px" data-testid="multi-upload-page">
      {/* Batch tags (FR-059) — single tag set applied to every file in the upload batch */}
      <Card mb="20px" data-testid="batch-tags-card">
        <CardBody>
          <Text as="label" mb={1} fontSize="sm" fontWeight="600" display="block">
            {t('admin.library.batchTagsLabel')}
          </Text>
          <TagMultiSelect
            value={batchTags}
            onChange={setBatchTags}
            suggestions={tagPool}
            allowCreate
            maxTags={20}
            size="md"
            wrapInFormControl={false}
            placeholder={t('admin.library.tagsPlaceholder')}
            inputTestId="batch-tag-input"
            isDisabled={isUploading}
          />
          <Text mt={2} fontSize="xs" color={secondaryColor}>
            {t('admin.library.batchTagsHelper')}
          </Text>
        </CardBody>
      </Card>

      {/* Pre-upload playlist picker (FR-075) — opens a side panel like the playlist "Add Tracks" drawer */}
      <Card mb="20px" data-testid="upload-playlists-card">
        <CardBody>
          <Flex align="center" justify="space-between" gap={3} wrap="wrap">
            <Box flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="600" color={textColor}>
                {t('admin.library.uploadToPlaylists.label')}
              </Text>
              <Text fontSize="xs" color={secondaryColor}>
                {selectedPlaylistIds.length > 0
                  ? t('admin.library.uploadToPlaylists.selected', { count: selectedPlaylistIds.length })
                  : t('admin.library.uploadToPlaylists.helper')}
              </Text>
              {selectedPlaylistNames && (
                <Text
                  fontSize="xs"
                  fontWeight="500"
                  color={textColor}
                  noOfLines={1}
                  mt={0.5}
                  title={selectedPlaylistNames}
                  data-testid="upload-playlists-names"
                >
                  {selectedPlaylistNames}
                </Text>
              )}
            </Box>
            <Button
              flexShrink={0}
              variant="outline"
              leftIcon={<MdQueueMusic />}
              onClick={playlistDrawer.onOpen}
              isDisabled={isUploading}
              data-testid="open-playlist-picker"
            >
              {t('admin.library.uploadToPlaylists.choose')}
            </Button>
          </Flex>
        </CardBody>
      </Card>

      {/* Playlist picker panel — mirrors the "Add Tracks" drawer on the playlist detail page */}
      <Drawer isOpen={playlistDrawer.isOpen} placement="right" size="md" onClose={playlistDrawer.onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{t('admin.library.uploadToPlaylists.label')}</DrawerHeader>
          <DrawerBody pb={6}>
            <PlaylistMultiSelect
              value={selectedPlaylistIds}
              onChange={setSelectedPlaylistIds}
              isDisabled={isUploading}
              hideLabel
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Drop zone */}
      <Card mb="20px">
        <div {...getRootProps()} data-testid="dropzone">
          <Flex
            direction="column"
            align="center"
            justify="center"
            p="40px"
            border="2px dashed"
            borderColor={isDragActive ? dropzoneActiveBorder : dropzoneBorder}
            borderRadius="20px"
            bg={isDragActive ? dropzoneActiveBg : dropzoneBg}
            cursor="pointer"
            _hover={{ borderColor: dropzoneActiveBorder, bg: dropzoneActiveBg }}
            transition="all 0.2s"
          >
            <input {...getInputProps()} data-testid="dropzone-input" />
            <Text fontSize="lg" fontWeight="700" color={textColor} mb="8px">
              {t('admin.library.dropzoneTitle')}
            </Text>
            <Text color={secondaryColor} mb="4px">
              {t('admin.library.dropzoneSubtitle')}
            </Text>
            <Text fontSize="sm" color={secondaryColor}>
              {t('admin.library.dropzoneAcceptedFormats')}
            </Text>
          </Flex>
        </div>
      </Card>

      {/* Rejected files message */}
      {rejectedMessage && (
        <Card mb="20px" bg="red.50" data-testid="rejected-files-message">
          <CardBody>
            <Text color="red.500" fontSize="sm">
              {rejectedMessage}
            </Text>
          </CardBody>
        </Card>
      )}

      {/* File queue */}
      {queue.length > 0 && (
        <Card mb="20px" p="0" overflow="hidden" data-testid="file-queue">
          <Flex p="12px 16px" bg={headerBg} borderRadius="20px 20px 0 0" gap={2}>
            <Text flex={2} fontWeight="700" fontSize="xs" color={secondaryColor} textTransform="uppercase" letterSpacing="wider">
              File
            </Text>
            <Text flex={2} fontWeight="700" fontSize="xs" color={secondaryColor} textTransform="uppercase" letterSpacing="wider">
              {t('admin.library.trackTitle')}
            </Text>
            <Text flex={1} fontWeight="700" fontSize="xs" color={secondaryColor} textTransform="uppercase" letterSpacing="wider" textAlign="right">
              Size
            </Text>
            <Text flex={1} fontWeight="700" fontSize="xs" color={secondaryColor} textTransform="uppercase" letterSpacing="wider" textAlign="center">
              Status
            </Text>
            <Box w="90px" />
          </Flex>

          {queue.map((item) => (
            <FileRow
              key={item.id}
              item={item}
              onRemove={() => dispatch({ type: 'REMOVE_FILE', id: item.id })}
              onTitleChange={(title) => dispatch({ type: 'UPDATE_TITLE', id: item.id, title })}
              disabled={isUploading}
            />
          ))}
        </Card>
      )}

      {/* Actions */}
      {queue.length > 0 && (
        <Flex gap={3}>
          <Button
            variant="brand"
            onClick={handleUploadAll}
            isDisabled={isUploading || !hasPending}
            data-testid="btn-upload-all"
          >
            {isUploading ? <Spinner size="sm" /> : t('admin.library.uploadAll')}
          </Button>
          {hasDone && (
            <Button
              variant="outline"
              onClick={() => dispatch({ type: 'CLEAR_COMPLETED' })}
              data-testid="btn-clear-completed"
            >
              {t('admin.library.clearCompleted')}
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
}
